// Admin → Voice → SIP Trunks
//
// Reuses the generic CRUD shell but adds three live-state columns
// (State · Channels · Probe) polled every 5s from /api/admin/telephony/status.
// Also shows an ARI-unreachable banner with a "Diagnose" button when the
// backend can't reach Asterisk so the admin sees actionable troubleshooting.
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCcw, WifiOff,
  Stethoscope, Wifi,
} from "lucide-react";
import api from "@/lib/api";
import ResourceCRUDPage from "@/admin/ResourceCRUDPage";

const STATE_TONE = {
  online:  { cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40", icon: CheckCircle2, label: "Registered" },
  offline: { cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40",                  icon: XCircle,        label: "Offline" },
  unknown: { cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",          icon: AlertTriangle,  label: "Unknown" },
};

function StatePill({ state }) {
  const tone = STATE_TONE[state] || STATE_TONE.unknown;
  const Icon = tone.icon;
  return (
    <Badge variant="outline" className={`${tone.cls} text-[11px] gap-1 px-2 py-0.5`}>
      <Icon className="w-3 h-3" />{tone.label}
    </Badge>
  );
}

function ARIBanner({ liveData }) {
  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);

  if (!liveData) return null;
  const mode = liveData.mode || "loading";
  const reachable = liveData.ari_reachable;
  const err = liveData.ari_error;

  // Status OK — show one-line green pill
  if (reachable === true && mode === "live") {
    return (
      <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 text-xs flex items-center gap-2" data-testid="ari-banner-ok">
        <Wifi className="w-3.5 h-3.5" /> ARI reachable
        {liveData.asterisk_version && <span className="ml-1 font-mono">· Asterisk {liveData.asterisk_version}</span>}
      </div>
    );
  }

  // Simulator mode — quiet info
  if (mode === "simulator") {
    return (
      <div className="px-4 py-2 bg-amber-50/50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 text-xs flex items-center gap-2" data-testid="ari-banner-sim">
        <AlertTriangle className="w-3.5 h-3.5" />
        Telephony in <strong>simulator mode</strong>. Set <code className="font-mono bg-amber-500/20 px-1 rounded">TELEPHONY_MODE=live</code> in your <code className="font-mono bg-amber-500/20 px-1 rounded">.env</code> to query real Asterisk state.
      </div>
    );
  }

  // ARI unreachable — error + Diagnose button
  return (
    <div className="px-4 py-3 bg-red-50/50 dark:bg-red-950/20 border-l-4 border-l-red-500" data-testid="ari-banner-error">
      <div className="flex items-start gap-2">
        <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-red-700 dark:text-red-300">
            Cannot reach Asterisk ARI — trunk registration states are unavailable.
          </div>
          {err && (
            <div className="text-[11px] font-mono text-red-700/80 dark:text-red-400/80 mt-1 break-all">
              {err}
            </div>
          )}
          <Button
            data-testid="diagnose-btn"
            onClick={async () => {
              setDiagBusy(true);
              try {
                const r = await api.get("/admin/telephony/diagnose");
                setDiag(r.data);
                toast.info("Diagnostics ready", { description: r.data?.hint || "See below" });
              } catch (e) {
                toast.error(e?.response?.data?.detail || e.message);
              } finally {
                setDiagBusy(false);
              }
            }}
            size="sm" variant="outline" className="mt-2 gap-1.5 h-7 text-xs"
          >
            {diagBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
            Diagnose
          </Button>
          {diag && (
            <pre className="mt-2 p-2 rounded bg-card border border-border text-[10px] font-mono text-foreground overflow-x-auto max-h-64" data-testid="diagnose-output">
              {JSON.stringify(diag, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SIPTrunksPage() {
  const probe = async (trunkId, trunkName) => {
    try {
      const r = await api.post(`/admin/telephony/trunks/${trunkId}/probe`);
      const s = r.data?.state || "unknown";
      const ok = s === "online";
      toast[ok ? "success" : (s === "offline" ? "error" : "info")](
        `Probe ${trunkName}: ${s.toUpperCase()}`,
        { description: r.data?.error || r.data?.detail || `${r.data?.channel_ids?.length || 0} active channel(s)` },
      );
    } catch (e) {
      toast.error("Probe failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const liveStatus = {
    endpoint: "/admin/telephony/status",
    intervalMs: 5000,
    extractRowState: (data, row) => (data?.trunks || []).find((t) => t.id === row.id),
    columns: [
      {
        key: "state", label: "Live State",
        render: (live) => <StatePill state={live?.state || "unknown"} />,
      },
      {
        key: "channels", label: "Channels",
        render: (live, row) => (
          <span className="tabular-nums font-mono text-xs" data-testid={`channel-count-${row.id}`}>
            {live?.channel_count ?? 0}<span className="text-muted-foreground"> / {row.data?.max_channels || 30}</span>
          </span>
        ),
      },
      {
        key: "probe", label: "",
        render: (_live, row) => (
          <Button
            data-testid={`probe-btn-${row.id}`}
            onClick={() => probe(row.id, row.name)}
            size="icon" variant="ghost" className="h-8 w-8" title="Probe registration state"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
        ),
      },
    ],
  };

  return (
    <ResourceCRUDPage
      kind="sip_trunks"
      liveStatus={liveStatus}
      banner={(liveData) => <ARIBanner liveData={liveData} />}
    />
  );
}
